import { useState } from "react";
import { EyeOff, HelpCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { DEFAULT_TARGET_MARGIN, marginTone, priceToReachTarget, type MarginTone } from "@workspace/totals";

export const PARENT_CATEGORIES = ["Labor", "Materials", "Fees"] as const;
export type ParentCategory = typeof PARENT_CATEGORIES[number];

export function costTypeToParent(type: string | null | undefined): ParentCategory {
  const t = (type ?? "materials").toLowerCase();
  if (t === "labour") return "Labor";
  if (t === "fees") return "Fees";
  return "Materials";
}

export function breakdownFromMaterials(
  materials: { type: string | null | undefined; totalCost: number }[],
): Record<ParentCategory, number> {
  const totals: Record<ParentCategory, number> = { Labor: 0, Materials: 0, Fees: 0 };
  for (const m of materials) {
    totals[costTypeToParent(m.type)] += m.totalCost;
  }
  return totals;
}

export type InternalWorkshopViewProps = {
  profit: number | null | undefined;
  margin: number | null | undefined;
  internalCost: number | null | undefined;
  breakdown: Record<ParentCategory, number> | null;
  formatCurrency: (value: number) => string;
  className?: string;
  enableTooltips?: boolean;
  showFooterHint?: boolean;
  /** Target margin (%) for this quote; colours the figures and drives the bar and hint. */
  target?: number;
  /** The default target from Settings, offered when the quote has its own. */
  defaultTarget?: number;
  /** True when the quote has its own target rather than the default. */
  ownTarget?: boolean;
  /** Lets the target be changed for this quote; null goes back to the default. */
  onTargetChange?: (target: number | null) => void;
};

// Colours on the navy card, by marginTone().
const TONE_ON_DARK: Record<MarginTone, string> = {
  good: "text-green-400",
  thin: "text-amber-400",
  low: "text-red-400",
};
const TONE_BAR: Record<MarginTone, string> = {
  good: "bg-green-400",
  thin: "bg-amber-400",
  low: "bg-red-400",
};

export function InternalWorkshopView({
  profit,
  margin,
  internalCost,
  breakdown,
  formatCurrency,
  className,
  enableTooltips = true,
  showFooterHint = true,
  target = DEFAULT_TARGET_MARGIN,
  defaultTarget = DEFAULT_TARGET_MARGIN,
  ownTarget = false,
  onTargetChange,
}: InternalWorkshopViewProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(target));
  const draftValue = Number(draft);
  const draftValid = draft.trim() !== "" && Number.isInteger(draftValue) && draftValue >= 0 && draftValue <= 90;
  const saveDraft = () => {
    if (!draftValid || !onTargetChange) return;
    // Typing the default back in clears the quote's own target.
    onTargetChange(draftValue === defaultTarget ? null : draftValue);
    setEditing(false);
  };
  const profitValue = profit ?? 0;
  const marginValue = margin ?? 0;
  const costValue = internalCost ?? 0;
  const tone = marginTone(marginValue, target);
  const colorClass = () => TONE_ON_DARK[tone];

  // The bar runs from 0% to 100% margin, with a marker at the target.
  // Only meaningful once there are costs (with none, the margin is 100%).
  const showTarget = costValue > 0;
  const fillPct = Math.min(100, Math.max(0, marginValue));
  const targetPct = Math.min(100, target);
  const shortBy = showTarget ? priceToReachTarget(profitValue + costValue, costValue, target) : 0;

  const hasBreakdown =
    breakdown !== null &&
    (breakdown.Labor > 0 || breakdown.Materials > 0 || breakdown.Fees > 0);

  return (
    <div
      className={cn(
        "bg-sidebar rounded-lg p-5 border border-sidebar-border text-sidebar-foreground shadow-sm relative overflow-hidden",
        className,
      )}
    >
      <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
      <div className="flex items-center gap-2 text-primary font-semibold tracking-tight mb-4">
        <EyeOff className="w-4 h-4" />
        Internal Workshop
        <Badge variant="outline" className="ml-auto text-[10px] uppercase tracking-wider text-sidebar-foreground/60 border-sidebar-border">
          Private
        </Badge>
      </div>
      {/* Profit / Margin */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="flex items-center gap-1 mb-1">
            <p className="text-xs text-sidebar-foreground/60 uppercase tracking-wider font-medium">Est. Profit</p>
            {enableTooltips && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="How is estimated profit calculated?"
                    className="text-sidebar-foreground/50 hover:text-sidebar-foreground/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
                  >
                    <HelpCircle className="w-3 h-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[240px] text-center">
                  Quote subtotal minus discounts (before tax) − Internal cost.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <p className={cn("text-lg font-bold tabular-nums", colorClass())}>
            {formatCurrency(profitValue)}
          </p>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-1 mb-1">
            <p className="text-xs text-sidebar-foreground/60 uppercase tracking-wider font-medium">Est. Margin</p>
            {enableTooltips && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="How is estimated margin calculated?"
                    className="text-sidebar-foreground/50 hover:text-sidebar-foreground/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
                  >
                    <HelpCircle className="w-3 h-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[260px] text-center">
                  Est. Profit divided by quote subtotal minus discounts (before tax), shown as a percentage.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <p className={cn("text-lg font-bold tabular-nums", colorClass())}>
            {marginValue.toFixed(1)}%
          </p>
        </div>
      </div>
      {/* Margin against the target */}
      {(showTarget || onTargetChange) && (
        <div className="mt-3">
          {showTarget && (
            <div className="relative h-1.5 rounded-full bg-sidebar-foreground/15">
              <div className={cn("absolute inset-y-0 left-0 rounded-full transition-[width] duration-500", TONE_BAR[tone])} style={{ width: `${fillPct}%` }} />
              <div className="absolute -top-1 -bottom-1 w-0.5 rounded bg-primary" style={{ left: `${targetPct}%` }} aria-hidden="true" />
            </div>
          )}
          {showTarget && shortBy != null && shortBy > 0.005 && (
            <p className="mt-1.5 text-[11px] leading-snug text-amber-300">
              Raise the price by {formatCurrency(shortBy)} to reach your {target}% target.
            </p>
          )}
          {editing ? (
            <form
              className="mt-2 flex items-center gap-1.5 text-[11px]"
              onSubmit={(e) => { e.preventDefault(); saveDraft(); }}
            >
              <span className="text-sidebar-foreground/70">Target for this quote</span>
              <span className="relative">
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={90}
                  step={1}
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }}
                  aria-label="Target margin for this quote, percent"
                  className="w-14 rounded border border-sidebar-border bg-sidebar-accent px-1.5 py-0.5 pr-4 text-right tabular-nums text-sidebar-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                />
                <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-sidebar-foreground/60">%</span>
              </span>
              <button type="submit" disabled={!draftValid} className="rounded px-1.5 py-0.5 font-semibold text-primary hover:bg-sidebar-accent disabled:opacity-40">Save</button>
              <button type="button" onClick={() => setEditing(false)} className="rounded px-1.5 py-0.5 text-sidebar-foreground/60 hover:bg-sidebar-accent">Cancel</button>
            </form>
          ) : (
            <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[11px] leading-snug text-sidebar-foreground/60">
              <span>Target {target}% · {ownTarget ? "this quote" : "default"}</span>
              {onTargetChange && (
                <>
                  <button
                    type="button"
                    onClick={() => { setDraft(String(target)); setEditing(true); }}
                    className="font-medium text-primary hover:underline"
                  >
                    Change
                  </button>
                  {ownTarget && (
                    <button
                      type="button"
                      onClick={() => onTargetChange(null)}
                      className="font-medium text-sidebar-foreground/70 hover:underline"
                    >
                      Use default ({defaultTarget}%)
                    </button>
                  )}
                </>
              )}
            </p>
          )}
        </div>
      )}
      {/* Cost Breakdown */}
      <div className="mt-4 border-t border-sidebar-border/40 pt-3 space-y-1.5">
        {hasBreakdown && breakdown && PARENT_CATEGORIES.map(cat => (
          <div key={cat} className="flex justify-between items-center text-xs">
            <span className="text-sidebar-foreground/70">{cat}</span>
            <span className="font-medium tabular-nums">{formatCurrency(breakdown[cat])}</span>
          </div>
        ))}
        <div
          className={cn(
            "flex justify-between items-center text-sm",
            hasBreakdown && "mt-2 pt-2 border-t border-sidebar-border/40",
          )}
        >
          <div className="flex items-center gap-1">
            <span className="text-sidebar-foreground/80 font-medium">Internal Cost</span>
            {enableTooltips && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="How is internal cost calculated?"
                    className="text-sidebar-foreground/50 hover:text-sidebar-foreground/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
                  >
                    <HelpCircle className="w-3 h-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[220px] text-center">
                  Total of all internal costs (labor, materials, fees) across line items.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <span className="font-semibold tabular-nums">{formatCurrency(internalCost ?? 0)}</span>
        </div>
      </div>
      {showFooterHint && (
        <p className="text-xs text-sidebar-foreground/50 mt-4 border-t border-sidebar-border/40 pt-3">
          Click "Costs &amp; Notes" on any line item to add internal costs and private notes.
        </p>
      )}
    </div>
  );
}
